import { useState, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { Modal, ModalHeader } from "../../../../globalComponents/ui/Modal";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";
import { PrimaryButton, SecondaryButton } from "../../../../globalComponents/ui/Button";

const SPREADSHEET_ACCEPT = ".csv,.xlsx,.xls";
const DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const ALL_ACCEPT = `${SPREADSHEET_ACCEPT},${DOCUMENT_ACCEPT}`;

const today = new Date().toISOString().split("T")[0];

const ALL_CALC_METHODS = [
  { id: "SPEND_BASED", label: "Spend Based", short: "Spend", icon: "💰", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  { id: "ACTIVITY_BASED", label: "Activity Based", short: "Activity", icon: "⚙️", color: "#0369a1", bg: "#eff6ff", border: "#bfdbfe" },
];

const EMISSION_KPI_TYPES = ["EMISSION", "EMISSION_ENERGY"];

const FieldLabel = ({ children, required }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 6 }}>
    {children}
    {required && <span style={{ color: "var(--red)", marginLeft: 2 }}>*</span>}
  </div>
);

const ProgressStrip = ({ pct }) => (
  <div style={{ height: 4, background: "var(--rule)", borderRadius: 2, overflow: "hidden", margin: "12px 0 4px" }}>
    <div style={{ height: "100%", borderRadius: 2, background: "linear-gradient(90deg,var(--accent),#60a5fa)", width: `${pct}%`, transition: "width 0.25s ease" }} />
  </div>
);

// ── Required Evidences banner ─────────────────────────────────────────────────
function RequiredEvidences({ requiredEvidences }) {
  if (!requiredEvidences || requiredEvidences.length === 0) return <></>;

  return (
    <div style={{
      margin: "0 0 0 0",
      background: "linear-gradient(135deg, #3f88a5 0%, #357a96 100%)",
      padding: "12px 24px 14px",
      borderBottom: "1px solid #0284c7",
    }}>
      {/* Label row */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: "0.02em" }}>
          Required Evidences
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: "1px 8px", borderRadius: 20,
          background: "rgba(255,255,255,0.3)", color: "#fff",
          marginLeft: 2,
        }}>
          {requiredEvidences.length}
        </span>
        <span style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", marginLeft: "auto" }}>
          ensure your document covers these
        </span>
      </div>

      {/* Evidence pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {requiredEvidences.map((ev, i) => (
          <div key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.45)",
            borderRadius: 8, padding: "6px 12px",
            backdropFilter: "blur(4px)",
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              background: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 800, color: "#0369a1",
            }}>
              {i + 1}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>{ev}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared Usage Distribution ─────────────────────────────────────────────────
const SharedUsageDistribution = ({ locationId, locations, ownershipControlMap }) => {
  if (!locationId) return null;
  const loc = locations.find(l => l.id === locationId);
  const control = ownershipControlMap[locationId];
  const pct = control?.percentage ?? null;
  const hasData = pct != null;

  const getBarColor = () => {
    if (!hasData) return "#cbd5e1";
    if (pct >= 50) return "#1D9E75";
    if (pct >= 20) return "#378ADD";
    return "#EF9F27";
  };

  const getBadgeStyle = () => {
    if (!hasData) return { color: "#94a3b8", background: "#f1f5f9" };
    if (pct >= 50) return { color: "#0F6E56", background: "#E1F5EE" };
    if (pct >= 20) return { color: "#185FA5", background: "#E6F1FB" };
    return { color: "#854F0B", background: "#FAEEDA" };
  };

  return (
    <div style={{ background: "#f8fafc", border: "1.5px solid #bae6fd", borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 13 }}>📊</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#0369a1" }}>Shared Usage</span>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
            {loc?.name || "Location"}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 9px", borderRadius: 20, flexShrink: 0, ...getBadgeStyle() }}>
            {hasData ? `${pct}%` : "No data"}
          </span>
        </div>
        <div style={{ height: 7, background: "var(--rule)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: hasData ? `${pct}%` : "0%", background: getBarColor(), borderRadius: 4, transition: "width 0.4s ease" }} />
        </div>
        {!hasData && (
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>ℹ No ownership record found</div>
        )}
      </div>
    </div>
  );
};

const EmissionMethodPicker = ({ availableMethods, selected, onChange, categoryName, scopeName }) => {
  const methods = ALL_CALC_METHODS.filter(m =>
    availableMethods.length === 0 || availableMethods.includes(m.id)
  );
  return (
    <div>
      <FieldLabel required>Calculation Method</FieldLabel>
      {categoryName && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "6px 10px", background: "#f0faf5", border: "1px solid #a5dfc3", borderRadius: 7 }}>
          <span style={{ fontSize: 12 }}>🌍</span>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#1baa6f" }}>{scopeName}</span>
            <span style={{ fontSize: 11, color: "#475569" }}> → {categoryName}</span>
          </div>
          {availableMethods.length > 0 && availableMethods.length < ALL_CALC_METHODS.length && (
            <span style={{ marginLeft: "auto", fontSize: 10, color: "#64748b", background: "#e8f5e9", borderRadius: 4, padding: "1px 7px", fontWeight: 600 }}>
              {availableMethods.length} method{availableMethods.length > 1 ? "s" : ""} configured
            </span>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {methods.map(m => {
          const isActive = selected === m.id;
          return (
            <button key={m.id} type="button" onClick={() => onChange(m.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, fontSize: 12, fontWeight: isActive ? 700 : 500, border: `2px solid ${isActive ? m.color : "#e2e8f0"}`, background: isActive ? m.bg : "#fafbfc", color: isActive ? m.color : "#64748b", cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit", boxShadow: isActive ? `0 0 0 3px ${m.color}22` : "none", flex: methods.length <= 2 ? 1 : "none" }}>
              <span style={{ fontSize: 16 }}>{m.icon}</span>
              <div style={{ textAlign: "left" }}>
                <div>{m.label}</div>
                <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>{m.id === "SPEND_BASED" ? "Cost / financial data" : "Physical activity data"}</div>
              </div>
              {isActive && <span style={{ marginLeft: "auto", fontSize: 13, color: m.color, flexShrink: 0 }}>✓</span>}
            </button>
          );
        })}
      </div>
      {!selected && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 6, fontWeight: 600 }}>⚠ Select a calculation method to continue</div>}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const AddDataModal = ({ show, onHide, item, financialYearId, locations = [], onSave, saveAnswer }) => {
  const [step, setStep] = useState("choose");
  const [permissionList, setPermissionList] = useState([]);

  const hasPermission = useCallback((permissionCode) => {
    return permissionList.some(p => p.permissionCode === permissionCode && p.checked);
  }, [permissionList]);

  useEffect(() => {
    const menu = JSON.parse(localStorage.getItem("menu") || "[]");
    const dmObject = menu.find(item => item.caption === "Data Management");
    if (dmObject?.permissions) setPermissionList(dmObject.permissions);
  }, []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [parseProgress, setParseProgress] = useState(0);
  const [parseError, setParseError] = useState("");
  const [parsedMeta, setParsedMeta] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [conflictPayload, setConflictPayload] = useState(null);
  const [locSearch, setLocSearch] = useState("");

  const [configuredMethods, setConfiguredMethods] = useState([]);
  const [emissionSettingsLoaded, setEmissionSettingsLoaded] = useState(false);
  const [selectedCalcMethod, setSelectedCalcMethod] = useState(null);
  const [ownershipControlMap, setOwnershipControlMap] = useState({});

  const isEmissionKpi = EMISSION_KPI_TYPES.includes(item?.kpi_type);
  const emissionCategoryId = item?.emission_category_id ?? null;
  const emissionCategoryName = item?.emission_category?.name ?? null;
  const emissionScopeName = item?.emission_category?.scopeName ?? item?.emission_category?.scope ?? null;

  const resolvedUnitObjs = item?.unitSetting?.supportedUnits?.length ? item.unitSetting.supportedUnits : [];
  const defaultUnit = item?.unitSetting?.selectedUnit ?? resolvedUnitObjs[0]?.unit ?? "";
  const defaultUnitId = item?.unitSetting?.selectedUnitId ?? resolvedUnitObjs.find(u => u.unit === defaultUnit)?.unit_id ?? null;
  const [requiredEvidences, setRequiredEvidences] = useState(null);

  const [form, setForm] = useState({
    location_id: null,
    sub_location_ids: [],
    from_date: "", to_date: "",
    value: "",
    unit: defaultUnit,
    unit_id: defaultUnitId,
    utility_type: "exclusive",
  });

  const [openLocDD, setOpenLocDD] = useState(false);
  const [openSubLocDD, setOpenSubLocDD] = useState(false);
  const locRef = useRef(null);
  const subLocRef = useRef(null);
  const fileRef = useRef(null);

  const selectedLocationHasConfig = form.location_id != null && !!ownershipControlMap[form.location_id];

  const fetchOwnershipControls = useCallback(async () => {
    const fyId = financialYearId || item?.financial_year_id;
    if (!fyId) return;
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl`, {}, { financialYearId: fyId }, "GET");
    if (!isSuccess || !Array.isArray(data)) return;
    const map = {};
    data.forEach(rec => {
      if (rec.isDisabled || rec.toDate) return;
      map[rec.locationId] = {
        percentage: rec.totalValue && rec.occupiedValue
          ? Number(((rec.occupiedValue / rec.totalValue) * 100).toFixed(2))
          : 0,
        ownership: rec,
      };
    });
    setOwnershipControlMap(map);
  }, [financialYearId, item]);

  const fetchEmissionSettings = useCallback(async () => {
    if (!isEmissionKpi || !emissionCategoryId) {
      setConfiguredMethods([]);
      setEmissionSettingsLoaded(true);
      return;
    }
    try {
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, {}, "GET");
      if (isSuccess) {
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        const row = list.find(r => r.emission_category_id === emissionCategoryId && r.is_enabled);
        if (row?.calculation_methods?.length > 0) {
          setConfiguredMethods(row.calculation_methods);
          if (row.calculation_methods.length === 1) setSelectedCalcMethod(row.calculation_methods[0]);
        } else {
          setConfiguredMethods([]);
        }
      }
    } catch {
      setConfiguredMethods([]);
    } finally {
      setEmissionSettingsLoaded(true);
    }
  }, [isEmissionKpi, emissionCategoryId]);

  const fetchRequiredEvidences = useCallback(async () => {
    if (!item?.id || !financialYearId) {
      setRequiredEvidences([]);
      return;
    }

    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}settings/kpis/required-evidences/${item.id}`,
      {},
      { fyid: financialYearId },
      "GET"
    )
      .then(({ isSuccess, data }) => {
        setRequiredEvidences(
          isSuccess && Array.isArray(data?.data?.requiredEvidences)
            ? data.data.requiredEvidences
            : []
        );
      })
      .catch(() => {
        setRequiredEvidences([]);
      });
  }, [item?.id, financialYearId]);

  useEffect(() => {
    if (show) {
      setStep("choose");
      setError(""); setParseError(""); setParsedMeta(null);
      setParseProgress(0); setDragOver(false);
      setOpenLocDD(false); setOpenSubLocDD(false);
      setConflictPayload(null); setSelectedCalcMethod(null);
      setConfiguredMethods([]); setEmissionSettingsLoaded(false);
      setOwnershipControlMap({});
      setForm({ location_id: null, sub_location_ids: [], from_date: "", to_date: "", value: "", unit: defaultUnit, unit_id: defaultUnitId, utility_type: "exclusive" });
      fetchEmissionSettings();
      fetchOwnershipControls();
      fetchRequiredEvidences();
    }
  }, [show, financialYearId, item, defaultUnit, defaultUnitId, fetchEmissionSettings, fetchOwnershipControls, fetchRequiredEvidences]);

  useEffect(() => {
    setForm(p => ({ ...p, sub_location_ids: [], utility_type: "exclusive" }));
  }, [form.location_id]); // eslint-disable-line

  useEffect(() => {
    const h = e => { if (locRef.current && !locRef.current.contains(e.target)) setOpenLocDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const h = e => { if (subLocRef.current && !subLocRef.current.contains(e.target)) setOpenSubLocDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selectedLocObj = locations.find(l => l.id === form.location_id) ?? null;
  const activeSubLocations = selectedLocObj?.location?.subLocation ?? [];
  const hasSubLocs = activeSubLocations.length > 0;

  const setField = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const selectLoc = (id) => {
    setForm(p => ({ ...p, location_id: p.location_id === id ? null : id, sub_location_ids: [], utility_type: "exclusive" }));
    setOpenLocDD(false);
  };

  const filteredLocations = locations.filter(l => l.name?.toLowerCase().includes(locSearch.toLowerCase()));

  const toggleSubLoc = (id) => setForm(p => ({
    ...p,
    sub_location_ids: p.sub_location_ids.includes(id) ? p.sub_location_ids.filter(x => x !== id) : [...p.sub_location_ids, id],
  }));

  const toggleAllSubLocs = () => setForm(p => ({
    ...p,
    sub_location_ids: p.sub_location_ids.length === activeSubLocations.length ? [] : activeSubLocations.map(s => s.id),
  }));

  const handleUnitChange = (unitString) => {
    const matched = resolvedUnitObjs.find(u => u.unit === unitString);
    setForm(p => ({ ...p, unit: unitString, unit_id: matched?.unit_id ?? null }));
  };

  const locLabel = () => {
    if (!form.location_id) return "Select a location…";
    return locations.find(l => l.id === form.location_id)?.name ?? "Location";
  };

  const subLocLabel = () => {
    const { sub_location_ids: ids } = form;
    if (!ids.length) return "Select sub-locations…";
    if (ids.length === activeSubLocations.length) return "All sub-locations";
    const names = ids.map(id => {
      const sl = activeSubLocations.find(s => s.id === id);
      return sl ? `${selectedLocObj?.name}-${sl.subLocation}` : null;
    }).filter(Boolean);
    return names.length <= 2 ? names.join(", ") : `${names[0]}, +${names.length - 1} more`;
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setParseError("");
    setStep("loading");
    setParseProgress(10);
    await uploadDocumentViaApi(file);
  }, []); // eslint-disable-line

  const uploadDocumentViaApi = async (file) => {
    try {
      setParseProgress(20);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("shouldParseDocument", "false");
      setParseProgress(40);
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}documents/upload`, {}, formData, "POST");
      setParseProgress(85);
      if (!isSuccess || !data) {
        setParseError("The server could not upload this document. Try a different file or enter manually.");
        setStep("choose");
        return;
      }
      setParseProgress(100);
      await new Promise(r => setTimeout(r, 150));
      setParsedMeta({
        source: "api", value: "", from_date: "", to_date: "", unit: defaultUnit,
        location_id: null, location_name: null,
        missing: ["value", "from_date", "to_date"],
        fileMetadataId: data.fileMetadataId,
        uploadedFileMetadata: {
          documentType: data.documentType,
          fileMetadataId: data.fileMetadataId,
          fileMetadata: data.fileMetadata,
          isExisting: data.isExisting,
        },
      });
      setStep("form");
    } catch {
      setParseError("Upload failed. Check your connection and try again.");
      setStep("choose");
    } finally {
      setParseProgress(0);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const buildPayload = () => {
    const resolvedFyId = financialYearId || item.financial_year_id;
    const subToLocMap = {};
    activeSubLocations.forEach(sl => {
      if (form.sub_location_ids.includes(sl.id)) subToLocMap[sl.id] = form.location_id;
    });
    return {
      financial_year_id: resolvedFyId,
      location_ids: [form.location_id],
      ...(form.sub_location_ids.length ? { sub_location_ids: form.sub_location_ids, sub_location_to_location_map: subToLocMap } : {}),
      module: item.module,
      questionId: item.id,
      from_date: form.from_date,
      to_date: form.to_date,
      value: parseFloat(form.value),
      unit: form.unit,
      unit_id: form.unit_id,
      utility_type: selectedLocationHasConfig ? form.utility_type : "exclusive",
      ...(isEmissionKpi && selectedCalcMethod ? { emission_category_id: emissionCategoryId, calculation_method: selectedCalcMethod } : {}),
      ...(parsedMeta?.uploadedFileMetadata ? { uploadedDocument: parsedMeta.uploadedFileMetadata } : {}),
    };
  };

  const callSaveApi = payload =>
    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}quantative-data`, {}, payload, "POST", false, false);

  const handleSave = async (isConfirmed = false) => {
    setError("");
    if (!isConfirmed) {
      if (!item) return setError("Invalid KPI item.");
      if (!form.location_id) return setError("Select a location.");
      if (!form.from_date || !form.to_date) return setError("Date range is required.");
      if (new Date(form.from_date) > new Date(form.to_date)) return setError("From date cannot be after to date.");
      if (form.value === "" || isNaN(form.value)) return setError("Value must be a valid number.");
      if (isEmissionKpi && !selectedCalcMethod) return setError("Select a calculation method for this emission KPI.");
      const resolvedFyId = financialYearId || item.financial_year_id;
      if (!resolvedFyId) return setError("Financial year could not be determined. Please refresh.");
    }
    const payload = isConfirmed && conflictPayload
      ? { ...conflictPayload, confirmOverwrite: true }
      : buildPayload();
    setSaving(true);
    try {
      const result = await callSaveApi(payload);
      if (result?.status === 409 || result?.statusCode === 409) {
        setConflictPayload(payload);
        setStep("conflict");
        return;
      }
      if (!result?.isSuccess) {
        setError(result?.message || "Failed to save data. Please check your inputs.");
        if (step === "conflict") setStep("form");
        return;
      }
      onSave?.(item, payload);
      onHide();
    } catch {
      setError("A network error occurred. Please try again.");
      if (step === "conflict") setStep("form");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const hasParsed = !!parsedMeta;
  const isWarn = field => hasParsed && parsedMeta.missing.includes(field);
  const hasUploadedDoc = hasParsed && !!parsedMeta.uploadedFileMetadata;
  const stepIndex = step === "form" || step === "conflict" ? 1 : 0;

  const subLocSummary = form.sub_location_ids.length
    ? form.sub_location_ids.length === activeSubLocations.length
      ? "All sub-locations"
      : form.sub_location_ids.map(id => {
          const sl = activeSubLocations.find(s => s.id === id);
          return sl ? `${selectedLocObj?.name}-${sl.subLocation}` : null;
        }).filter(Boolean).join(", ")
    : null;

  const availableMethodIds = configuredMethods.length > 0
    ? configuredMethods
    : ALL_CALC_METHODS.map(m => m.id);

  const resolvedFyId = financialYearId || item?.financial_year_id;

  return (
    <Modal show={show} onClose={onHide} maxWidth={540}>
      <ModalHeader emoji="➕" title="Add Data" subtitle={item?.kpi_name} onClose={onHide} />

      {!hasPermission("ADD_KPI_DATA_RECORD") && !hasPermission("EDIT_KPI_DATA_RECORD") && permissionList.length > 0 ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>No Permission</div>
          <div style={{ fontSize: 14, color: "var(--ink-4)" }}>You do not have permission to add or edit data.</div>
        </div>
      ) : (
        <>
          {/* Required evidences — always visible, full-width, right under the header */}
          { (step === "choose" || step === "loading") && (
            <RequiredEvidences requiredEvidences={requiredEvidences} />
          )}

          {/* Step indicator */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, padding: "16px 24px 14px" }}>
            {["Upload", "Fill & Save"].map((label, i) => {
              const active = i === stepIndex;
              const done = i < stepIndex;
              return (
                <div key={label} style={{ display: "flex", alignItems: "center", flex: i === 1 ? 1 : "none" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", fontSize: 10, fontWeight: 800, fontFamily: "var(--font-mono)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: done ? "var(--green)" : active ? "var(--accent)" : "var(--rule)", transition: "background 0.2s" }}>
                      {done ? "✓" : i + 1}
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", color: active ? "var(--accent)" : "var(--ink-4)" }}>{label}</span>
                  </div>
                  {i === 0 && <div style={{ width: 40, height: 2, borderRadius: 1, margin: "0 8px 14px", background: done ? "var(--green)" : "var(--rule)", transition: "background 0.2s" }} />}
                </div>
              );
            })}
          </div>

          <div style={{ padding: "0 24px 24px" }}>

            {/* ══ CHOOSE / LOADING ══ */}
            {(step === "choose" || step === "loading") && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => step !== "loading" && fileRef.current?.click()}
                  style={{ border: `2px dashed ${dragOver ? "var(--accent)" : "var(--rule)"}`, borderRadius: 12, padding: "32px 20px", textAlign: "center", cursor: step === "loading" ? "default" : "pointer", background: dragOver ? "var(--accent-3)" : "var(--surface-2)", transition: "all 0.15s ease" }}
                >
                  <input ref={fileRef} type="file" hidden accept={ALL_ACCEPT} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
                  {step === "loading" ? (
                    <>
                      <div style={{ fontSize: 34, marginBottom: 10 }}>⚙️</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)", marginBottom: 4 }}>Uploading your file…</div>
                      <ProgressStrip pct={parseProgress} />
                      <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 6, fontFamily: "var(--font-mono)" }}>{parseProgress}%</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 38, marginBottom: 10 }}>📂</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)", marginBottom: 8 }}>Drop a file or click to browse</div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginBottom: 12 }}>
                        {[{ icon: "📊", label: "XLSX / CSV", sub: "auto-fills form" }, { icon: "📄", label: "PDF / DOC", sub: "stored as proof" }, { icon: "🖼️", label: "JPG / PNG", sub: "stored as proof" }].map(({ icon, label, sub }) => (
                          <div key={label} style={{ padding: "6px 12px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--rule)", textAlign: "left" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-2)" }}>{icon} {label}</div>
                            <div style={{ fontSize: 10, color: "var(--ink-4)" }}>{sub}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 5, justifyContent: "center", flexWrap: "wrap" }}>
                        {["Value", "From Date", "To Date", "Unit", "Location"].map(col => (
                          <span key={col} style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: "var(--surface-3)", color: "var(--ink-3)", fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{col}</span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {parseError && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600 }}>
                    ⚠️ {parseError}
                  </div>
                )}

                {step !== "loading" && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
                      <span style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>OR</span>
                      <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
                    </div>
                    <SecondaryButton onClick={() => setStep("form")} style={{ width: "100%", padding: "10px 0", fontSize: 13 }}>
                      ✏️ Enter Manually
                    </SecondaryButton>
                  </>
                )}
              </div>
            )}

            {/* ══ CONFLICT ══ */}
            {step === "conflict" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20, textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 52 }}>⚠️</div>
                <div>
                  <h3 style={{ margin: "0 0 10px", fontSize: 18, color: "var(--ink)" }}>Data Already Exists</h3>
                  <p style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: "1.6", margin: 0 }}>
                    A record already exists for the selected location within this date range. You can go back and adjust your inputs, or save this new record alongside the existing one.
                  </p>
                </div>
                <div style={{ background: "var(--surface-2)", padding: "12px 16px", borderRadius: 10, fontSize: 12, textAlign: "left", border: "1px solid var(--rule)", lineHeight: "1.8" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: "var(--ink-2)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)" }}>Summary</div>
                  <div>📍 {locations.find(l => l.id === form.location_id)?.name || "Location"}</div>
                  {subLocSummary && <div>🏢 Sub-locations: {subLocSummary}</div>}
                  <div>📅 {form.from_date} → {form.to_date}</div>
                  <div>📊 {form.value} {form.unit}</div>
                  {selectedLocationHasConfig && <div>🔌 Utility: {form.utility_type === "shared" ? "Shared" : "Exclusive"}</div>}
                  {isEmissionKpi && selectedCalcMethod && <div>🧮 Method: {ALL_CALC_METHODS.find(m => m.id === selectedCalcMethod)?.label || selectedCalcMethod}</div>}
                </div>
                {error && (
                  <div style={{ display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600, alignItems: "center", textAlign: "left" }}>
                    ⚠️ {error}
                  </div>
                )}
                <div style={{ display: "flex", gap: 12 }}>
                  <SecondaryButton style={{ flex: 1 }} disabled={saving} onClick={() => { setError(""); setStep("form"); }}>← Back to Edit</SecondaryButton>
                  <PrimaryButton style={{ flex: 1, background: "var(--red)", borderColor: "var(--red)" }} disabled={saving} onClick={() => handleSave(true)}>
                    {saving
                      ? <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Saving…</span>
                      : "Yes, Save Anyway"}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* ══ FORM ══ */}
            {step === "form" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {hasUploadedDoc && (
                  <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #bfdbfe" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#eff6ff" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 16 }}>📎</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "var(--ink)" }}>File uploaded — saved as proof document</div>
                          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                            {parsedMeta.uploadedFileMetadata.fileMetadata?.fileName} · {Math.round(Number(parsedMeta.uploadedFileMetadata.fileMetadata?.fileSize || 0) / 1024)} KB
                          </div>
                        </div>
                      </div>
                      <button onClick={() => { setStep("choose"); setParsedMeta(null); setError(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-sans)", padding: "2px 6px", flexShrink: 0 }}>
                        ← try another file
                      </button>
                    </div>
                    <div style={{ padding: "8px 14px", background: "var(--surface)", fontSize: 11, color: "var(--ink-3)" }}>
                      ℹ️ Fill in the data fields below manually — this document will be attached as proof.
                    </div>
                  </div>
                )}

                {!hasParsed && (
                  <button onClick={() => setStep("choose")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--accent)", fontWeight: 700, padding: 0, fontFamily: "var(--font-sans)", alignSelf: "flex-start" }}>
                    ← Upload file instead
                  </button>
                )}

                {/* EMISSION METHOD */}
                {isEmissionKpi && (
                  <div style={{ padding: "14px 16px", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10 }}>
                    {!emissionSettingsLoaded ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-4)" }}>
                        <span style={{ width: 12, height: 12, border: "2px solid #e2e8f0", borderTopColor: "#1abc9c", borderRadius: "50%", display: "inline-block", animation: "kpiSpin 0.7s linear infinite" }} />
                        Loading calculation methods…
                      </div>
                    ) : (
                      <EmissionMethodPicker
                        availableMethods={availableMethodIds}
                        selected={selectedCalcMethod}
                        onChange={setSelectedCalcMethod}
                        categoryName={emissionCategoryName}
                        scopeName={emissionScopeName}
                      />
                    )}
                  </div>
                )}

                {/* LOCATION */}
                <div ref={locRef}>
                  <FieldLabel required>Location</FieldLabel>
                  <div className="kpi-input" style={{ cursor: "pointer", position: "relative", userSelect: "none" }} onClick={() => { setOpenLocDD(p => !p); setLocSearch(""); }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: form.location_id ? "var(--ink)" : "var(--ink-4)" }}>{locLabel()}</span>
                      <span style={{ fontSize: 10, color: "var(--ink-4)" }}>▾</span>
                    </div>
                    {openLocDD && (
                      <div style={{ position: "absolute", top: "110%", left: 0, right: 0, background: "var(--surface)", border: "1.5px solid var(--rule)", borderRadius: 10, maxHeight: 280, overflowY: "hidden", zIndex: 50, display: "flex", flexDirection: "column", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: "8px", borderBottom: "1px solid var(--rule)", background: "var(--surface-2)" }}>
                          <input autoFocus type="text" placeholder="Search locations..." value={locSearch} onChange={e => setLocSearch(e.target.value)} style={{ width: "100%", padding: "6px 10px", fontSize: "12px", borderRadius: "6px", border: "1px solid var(--rule)", outline: "none", fontFamily: "var(--font-sans)" }} />
                        </div>
                        <div style={{ overflowY: "auto", padding: 8, maxHeight: 220 }}>
                          {filteredLocations.length === 0 && (
                            <div style={{ padding: "20px 8px", fontSize: 12, color: "var(--ink-4)", textAlign: "center" }}>
                              {locations.length === 0 ? "No locations available" : "No matches found"}
                            </div>
                          )}
                          {filteredLocations.map(l => {
                            const isSelected = form.location_id === l.id;
                            const hasConfig = !!ownershipControlMap[l.id];
                            return (
                              <div key={l.id} onClick={() => selectLoc(l.id)} style={{ display: "flex", gap: 8, padding: "7px 8px", fontSize: 12, cursor: "pointer", borderRadius: 6, alignItems: "center", background: isSelected ? "var(--accent-3)" : "transparent" }}>
                                <span style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `2px solid ${isSelected ? "var(--accent)" : "var(--rule)"}`, background: isSelected ? "var(--accent)" : "transparent" }}>
                                  {isSelected && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", display: "inline-block" }} />}
                                </span>
                                <span style={{ flex: 1, fontWeight: isSelected ? 700 : 400, color: isSelected ? "var(--accent)" : "var(--ink)" }}>{l.name}</span>
                                <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
                                  {hasConfig && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "#e0f2fe", color: "#0369a1", fontFamily: "var(--font-mono)" }}>ownership</span>}
                                  {l.location?.subLocation?.length > 0 && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "var(--accent-3)", color: "var(--accent)", fontFamily: "var(--font-mono)" }}>{l.location.subLocation.length} sub</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* SUB-LOCATION */}
                {hasSubLocs && (
                  <div ref={subLocRef}>
                    <FieldLabel>Sub-Location</FieldLabel>
                    <div className="kpi-input" style={{ cursor: "pointer", position: "relative", userSelect: "none" }} onClick={() => setOpenSubLocDD(p => !p)}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: form.sub_location_ids.length ? "var(--ink)" : "var(--ink-4)" }}>{subLocLabel()}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {form.sub_location_ids.length > 0 && <span onClick={e => { e.stopPropagation(); setField("sub_location_ids", []); }} style={{ fontSize: 12, color: "var(--ink-4)", cursor: "pointer", lineHeight: 1 }} title="Clear">✕</span>}
                          <span style={{ fontSize: 10, color: "var(--ink-4)" }}>▾</span>
                        </div>
                      </div>
                      {openSubLocDD && (
                        <div style={{ position: "absolute", top: "110%", left: 0, right: 0, background: "var(--surface)", border: "1.5px solid var(--rule)", borderRadius: 10, maxHeight: 200, overflowY: "auto", zIndex: 50, padding: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
                          <label style={{ display: "flex", gap: 8, padding: "6px 8px", fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: 6, alignItems: "center" }}>
                            <input type="checkbox" checked={form.sub_location_ids.length === activeSubLocations.length && activeSubLocations.length > 0} onChange={toggleAllSubLocs} />
                            Select All Sub-Locations
                          </label>
                          <div style={{ height: 1, background: "var(--rule)", margin: "4px 0" }} />
                          {activeSubLocations.map(sl => (
                            <label key={sl.id} style={{ display: "flex", gap: 8, padding: "6px 8px", fontSize: 12, cursor: "pointer", borderRadius: 6, alignItems: "center", background: form.sub_location_ids.includes(sl.id) ? "var(--accent-3)" : "transparent" }}>
                              <input type="checkbox" checked={form.sub_location_ids.includes(sl.id)} onChange={() => toggleSubLoc(sl.id)} />
                              <span style={{ flex: 1, fontWeight: form.sub_location_ids.includes(sl.id) ? 700 : 400, color: form.sub_location_ids.includes(sl.id) ? "var(--accent)" : "var(--ink)" }}>{`${selectedLocObj?.name}-${sl.subLocation}`}</span>
                              {form.sub_location_ids.includes(sl.id) && <span style={{ fontSize: 10, color: "var(--accent)" }}>✓</span>}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 4 }}>Optional — leave blank to apply to all sub-locations</div>
                  </div>
                )}

                {/* UTILITY TYPE */}
                {selectedLocationHasConfig && (
                  <div>
                    <FieldLabel required>Utility Type</FieldLabel>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[{ id: "exclusive", label: "Exclusive" }, { id: "shared", label: "Shared" }].map(opt => {
                        const active = form.utility_type === opt.id;
                        return (
                          <button key={opt.id} type="button" onClick={() => setField("utility_type", opt.id)} style={{ padding: "10px", borderRadius: 10, border: `2px solid ${active ? "var(--accent)" : "var(--rule)"}`, background: active ? "var(--accent-3)" : "var(--surface)", textAlign: "left", cursor: "pointer" }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{opt.label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* SHARED USAGE DISTRIBUTION */}
                {selectedLocationHasConfig && form.utility_type === "shared" && form.location_id && (
                  <SharedUsageDistribution locationId={form.location_id} locations={locations} ownershipControlMap={ownershipControlMap} />
                )}

                {/* DATE RANGE */}
                <div>
                  <FieldLabel required>Date Range</FieldLabel>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 4 }}>FROM</div>
                      <input type="date" className="kpi-input" style={isWarn("from_date") ? { borderColor: "var(--amber)", background: "var(--amber-2)" } : {}} max={today} value={form.from_date} onChange={e => setField("from_date", e.target.value)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 4 }}>TO</div>
                      <input type="date" className="kpi-input" style={isWarn("to_date") ? { borderColor: "var(--amber)", background: "var(--amber-2)" } : {}} max={today} value={form.to_date} onChange={e => setField("to_date", e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* VALUE + UNIT */}
                <div>
                  <FieldLabel required>Value</FieldLabel>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 8 }}>
                    <input type="number" className="kpi-input" style={isWarn("value") ? { borderColor: "var(--amber)", background: "var(--amber-2)" } : {}} placeholder="Enter numeric value" value={form.value} onChange={e => setField("value", e.target.value)} />
                    <select className="kpi-input" value={form.unit} onChange={e => handleUnitChange(e.target.value)}>
                      {resolvedUnitObjs.map(u => <option key={u.unit_id} value={u.unit}>{u.unit}</option>)}
                    </select>
                  </div>
                  {isWarn("value") && <div style={{ fontSize: 11, color: "var(--amber)", marginTop: 4, fontWeight: 600 }}>Value not found in file — enter manually</div>}
                </div>

                {error && (
                  <div style={{ display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600, alignItems: "center" }}>
                    ⚠️ {error}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
                  <SecondaryButton onClick={onHide}>Cancel</SecondaryButton>
                  <PrimaryButton onClick={() => handleSave(false)} disabled={saving || (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)} style={{ minWidth: 90 }}>
                    {saving
                      ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Saving…</span>
                      : "Save"}
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddDataModal;